import { useDataTableContext } from '../context';
import { DataTableColumnMeta } from '../types';
import { validateColumnFilterType } from '../utils';
import { DateFilter } from './date-filter';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface ColumnDateFilterProps {
  columnId: string;
  /** Display label; suppressed when omitted (e.g. titled by parent). */
  label?: string;
  minDate?: string;
  maxDate?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  /** Enable native browser autofill on the date inputs. Defaults to true. */
  autoComplete?: boolean;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column-bound date range filter that syncs with DataTable's filter state.
 * Automatically validates column type compatibility and manages filter value.
 * @param {ColumnDateFilterProps} props - Component props
 * @param {string} props.columnId - ID of the column to filter
 * @param {string} props.label - Display label for the filter
 * @param {string} [props.minDate] - Minimum selectable date
 * @param {string} [props.maxDate] - Maximum selectable date
 * @param {string} [props.startPlaceholder] - Placeholder for start date input
 * @param {string} [props.endPlaceholder] - Placeholder for end date input
 * @throws {Error} If column type is not 'date'
 * @return {JSX.Element} The date filter element
 */
export function ColumnDateFilter({
  columnId,
  label,
  minDate,
  maxDate,
  startPlaceholder,
  endPlaceholder,
  autoComplete,
}: ColumnDateFilterProps) {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const meta = column?.columnDef.meta as DataTableColumnMeta | undefined;

  validateColumnFilterType('date', meta?.type, columnId);

  const value = (column?.getFilterValue() as [
    string | null,
    string | null,
  ]) ?? [null, null];

  const handleChange = (newValue: [string | null, string | null]) => {
    const isEmpty = !newValue[0] && !newValue[1];
    column?.setFilterValue(isEmpty ? undefined : newValue);
  };

  return (
    <DateFilter
      id={`${columnId}-filter`}
      label={label}
      value={value}
      onChange={handleChange}
      minDate={minDate}
      maxDate={maxDate}
      startPlaceholder={startPlaceholder}
      endPlaceholder={endPlaceholder}
      autoComplete={autoComplete}
    />
  );
}
